/**
 * Main/backup (container; wave 7b-tail C): the overview of every site the
 * caller may see — in the cloud from the nodes' last reports, on a node its
 * own site live — with the operations the backend serves: switch over (both),
 * manual only and "switch whole node" (node), and the emergency activation
 * (node, asked of the standby's node, confirmed in `ForcePromoteDialog`).
 * `siteId` narrows it to one site (the Admin area's site page).
 */
import { useNavigate } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import { toast } from 'sonner';
import { useConnectivity, useForcePromote, useGrants, usePairSwitchover, useRedundancyOverview, useSwitchoverAll, useUpdateRedundancy } from '../app/hooks.js';
import { productionHref } from '../components/productions/ProductionList.js';
import { ForcePromoteDialog } from '../components/redundancy/ForcePromoteDialog.js';
import { PairOverview } from '../components/redundancy/PairOverview.js';
import { allows } from '../lib/permissions.js';
import { type PairActions, pairActions, selfNodeIdOf } from '../lib/redundancy.js';

function messageOf(err: unknown): string {
  return err instanceof Error ? err.message : String(err);
}

export function RedundancyPanel({ siteId }: { readonly siteId: string | null }) {
  const overview = useRedundancyOverview(siteId);
  const grants = useGrants();
  const connectivity = useConnectivity();
  const switchover = usePairSwitchover();
  const manualOnly = useUpdateRedundancy();
  const switchAll = useSwitchoverAll();
  const force = useForcePromote();
  const navigate = useNavigate();
  const [forcing, setForcing] = useState<string | null>(null);
  const [pending, setPending] = useState<string | null>(null);

  const selfNodeId = selfNodeIdOf(connectivity);
  const sites = useMemo(() => overview.data?.sites ?? [], [overview.data]);
  const actions = useMemo(() => {
    const out: Record<string, PairActions> = {};
    for (const site of sites) for (const row of site.productions) out[row.productionId] = pairActions(row, site.siteId, grants, selfNodeId);
    return out;
  }, [sites, grants, selfNodeId]);
  const forcingRow = sites.flatMap((s) => s.productions).find((p) => p.productionId === forcing) ?? null;

  const settle = <T,>(work: Promise<T>, id: string, ok: (value: T) => string): void => {
    setPending(id);
    work.then(
      (value) => toast.success(ok(value)),
      (err: unknown) => toast.error(messageOf(err)),
    ).finally(() => setPending(null));
  };

  return (
    <>
      <PairOverview
        sites={sites}
        loading={overview.isFetching}
        error={overview.error === null ? null : messageOf(overview.error)}
        actions={actions}
        selfNodeId={selfNodeId}
        canSwitchoverAll={selfNodeId !== null && allows(grants, 'node.switchover', { siteId: null, studioId: null })}
        pending={pending}
        onRetry={() => {
          void overview.refetch();
        }}
        onOpen={(productionId) => {
          void navigate({ to: productionHref(productionId) });
        }}
        onSwitchover={(productionId) => settle(switchover.mutateAsync({ productionId }), productionId, (r) => `Switched over: ${r.from} → ${r.to} at term ${String(r.term)} in ${String(r.durationMs)} ms`)}
        onManualOnly={(productionId, on) =>
          settle(manualOnly.mutateAsync({ productionId, manualOnly: on }), productionId, (s) => (s.manualOnly ? 'Automatic failover off (manual only)' : 'Automatic failover on (witness-gated)'))
        }
        onForcePromote={(productionId) => {
          force.reset();
          setForcing(productionId);
        }}
        onSwitchoverAll={() => settle(switchAll.mutateAsync(), 'all', (r) => `${String(r.switched.length)} switched, ${String(r.failed.length)} not${r.failed.length > 0 ? `: ${r.failed.map((f) => f.reason).join('; ')}` : ''}`)}
      />
      <ForcePromoteDialog
        open={forcingRow !== null}
        confirmation={forcingRow?.name ?? forcingRow?.productionId ?? ''}
        activeNode={forcingRow?.activeNode ?? null}
        standbyNode={selfNodeId ?? '?'}
        pending={force.isPending}
        error={force.error === null ? null : messageOf(force.error)}
        onConfirm={({ confirm, reason }) => {
          if (forcing === null) return;
          force.mutate(
            { productionId: forcing, confirm, reason },
            {
              onSuccess: (r) => {
                setForcing(null);
                toast.warning(`FORCED on air on ${r.to} at term ${String(r.term)}. ${r.warning}`, { duration: 20_000 });
              },
            },
          );
        }}
        onOpenChange={(open) => (open ? undefined : setForcing(null))}
      />
    </>
  );
}

export function RedundancyView() {
  return (
    <div className="grid gap-4" data-testid="redundancy-page">
      <div className="grid gap-1">
        <h1 className="text-xl font-semibold">Main/backup</h1>
        <p className="text-sm text-muted-foreground">Every production with its active and standby node, graded, and what each node runs. Worst first.</p>
      </div>
      <RedundancyPanel siteId={null} />
    </div>
  );
}
